import React, { ReactElement } from 'react'
import { Flex, Text, ThemeUIStyleObject } from 'theme-ui'
import { Svg } from 'ui'
import TailscaleIcon from 'images/homePage/integrations/tailscale.svg'
import VerticalDivider from 'ui/VerticalDivider'
import NinjaRMMIcon from 'images/homePage/integrations/ninja-rmm.svg'
import MicrosiftIcon from 'images/homePage/integrations/microsoft-intune.svg'
import DattoRMMIcon from 'images/homePage/integrations/datto-rmm.svg'
import NAbleRMMIcon from 'images/homePage/integrations/n-able-rmm.svg'
import MicroSoftIcon from 'images/serviceCategories/opaqueOSIcons/microsoft-opaque.svg'
import AppleIcon from 'images/serviceCategories/opaqueOSIcons/apple-opaque.svg'
import LinuxIcon from 'images/serviceCategories/opaqueOSIcons/linux-opaque.svg'
import iOsIcon from 'images/serviceCategories/opaqueOSIcons/iOS-opaque.svg'
import AndroidIcon from 'images/serviceCategories/opaqueOSIcons/android-opaque.svg'
import WindowIcon from 'images/serviceCategories/opaqueOSIcons/window-delete-opaque.svg'
import RouterIcon from 'images/serviceCategories/opaqueOSIcons/router-opaque.svg'

const osIcons = [MicroSoftIcon, AppleIcon, LinuxIcon, iOsIcon, AndroidIcon, WindowIcon, RouterIcon]

const dnsTypes = ['DNS-over-HTTPS/3', 'DNS-over-TLS', 'DNS-over-QUIC', 'Legacy DNS']

const dividerStyles = {
  width: '0.2rem',
  height: '3.2rem',
  color: 'white10',
}

export default function IntegrationsSection({
  sx,
  bannerSx,
  shouldShowDnsAndIcons = true,
}: {
  sx?: ThemeUIStyleObject
  bannerSx?: ThemeUIStyleObject
  shouldShowDnsAndIcons?: boolean
}): ReactElement {
  return (
    <Flex
      sx={{
        flexDirection: 'column',
        gap: '4.8rem',
        ...sx,
      }}
    >
      {shouldShowDnsAndIcons && (
        <Flex sx={{ flexDirection: 'column' }}>
          <Text
            sx={{
              display: 'inline-block',
              textAlign: 'center',
              color: 'aliceBlue70',
              px: ['1.6rem', 'none'],
            }}
          >
            Integrates with all operating systems, all browsers, and most routers.
          </Text>
          <Flex
            sx={{
              gap: '0.8rem',
              flexWrap: 'wrap',
              justifyContent: 'center',
              pt: '1.6rem',
              px: ['1.6rem', 'none'],
            }}
          >
            {osIcons.map((Icon, index) => (
              <Flex
                key={index}
                sx={{
                  height: ['4rem', '4.8rem'],
                  width: ['4rem', '4.8rem'],
                  border: '1px solid',
                  borderColor: 'blueYonder20',
                  p: ['0', '1.2rem'],
                  borderRadius: '10rem',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Svg
                  sx={{
                    height: '2.4rem',
                    width: '2.4rem',
                  }}
                  svg={Icon}
                  descriptiveText={`Operating system icon ${index + 1}`}
                />
              </Flex>
            ))}
          </Flex>
          <Flex
            sx={{
              gap: '0.8rem',
              flexWrap: 'wrap',
              justifyContent: 'center',
              pt: '1.6rem',
              px: ['1.6rem', 'none'],
            }}
          >
            {dnsTypes.map((type, index) => (
              <Flex
                key={index}
                sx={{
                  fontSize: '1.3rem',
                  color: index === 3 ? 'aliceBlue70' : 'airSuperiorityBlue',
                  fontWeight: '500',
                  border: '1px solid',
                  borderColor: 'blueYonder20',
                  p: '0.8rem 1.2rem',
                  borderRadius: '10rem',
                  flexShrink: 0,
                  background:
                    'linear-gradient(0deg, rgba(92, 109, 150, 0.05), rgba(92, 109, 150, 0.05)),linear-gradient(0deg, rgba(92, 135, 150, 0.05), rgba(92, 135, 150, 0.05))',
                }}
              >
                <Text>{type}</Text>
              </Flex>
            ))}
          </Flex>
        </Flex>
      )}
      <Flex
        sx={{
          width: '100%',
          maxWidth: ['40rem', '60rem', '100%'],
          alignSelf: 'center',
          alignItems: 'center',
          justifyContent: 'center',
          flexWrap: 'wrap',
          columnGap: ['0.8rem', '3.2rem'],
          rowGap: '0.8rem',
          ...bannerSx,
        }}
      >
        <Svg svg={TailscaleIcon} descriptiveText="Tailscale integration icon" />
        <VerticalDivider sx={dividerStyles} />
        <Svg svg={NinjaRMMIcon} descriptiveText="NinjaRMM integration icon" />
        <VerticalDivider sx={{ ...dividerStyles, display: ['none', 'block', 'block'] }} />
        <Svg svg={MicrosiftIcon} descriptiveText="Microsoft integration icon" />
        <VerticalDivider sx={{ ...dividerStyles, display: ['block', 'none', 'block'] }} />
        <Svg svg={DattoRMMIcon} descriptiveText="DattoRMM integration icon" />
        <VerticalDivider sx={{ ...dividerStyles, display: ['none', 'block', 'block'] }} />
        <Svg
          sx={{
            mt: ['1.2rem', 0],
          }}
          svg={NAbleRMMIcon}
          descriptiveText="N-able RMM integration icon"
        />
      </Flex>
    </Flex>
  )
}
